import { useCallback, useEffect, useRef, useState } from "react";
import { JournalLalaSettingPanel } from "./JournalLalaSettingPanel";
import { JOURNAL_LALA_STATES, journalLalaState, journalLalaRequest } from "../journalLalaSetting.js";
import { t } from "../strings.js";

// LALA-BRAIN-1 CLAUSE 2 — **THE JOURNAL'S DOOR.** The state and the two round trips; the panel
// beside it is hookless and is what the gate CALLS.
//
// **IT READS THE SERVER, NEVER A CACHED GUESS.** The preference lives on her account and travels
// with her, so a second device must not start over and a stale local copy must not tell her that her
// journal is private when it is not.
export function JournalLalaSetting({ language = "en" }: { language?: string }) {
  // >>> **NULL IS "NOT LOOKED YET", NEVER "PRIVATE"** — and on this control that direction is the
  // whole reason for the third state. Drawing OFF while the setting is still loading would tell a
  // member her journal is closed to Lala at a moment when it is open, which is the most damaging
  // wrong thing this panel could say (P4-1's ruling, pointed at the most private thing on the site).
  const [answer, setAnswer] = useState<null | { on: boolean }>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const read = useCallback(async () => {
    const got = await fetch("/api/journal/lala-setting", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!alive.current) return;
    // A read that failed leaves the state UNKNOWN rather than inventing one, so the panel draws no
    // button — a control that cannot know what it would do must not offer to do it.
    if (got && typeof got.on === "boolean") setAnswer({ on: got.on });
  }, []);

  useEffect(() => { void read(); }, [read]);

  const act = useCallback(async (action: string) => {
    const body = journalLalaRequest(action);
    if (!body) return;
    setBusy(true); setNote("");
    const got = await fetch("/api/journal/lala-setting", {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!alive.current) return;
    setBusy(false);
    // R47/R53/R61: the answer is READ. A control that reports success on a refused write is the one
    // shape a member never reports, because her screen looked right.
    if (got && typeof got.on === "boolean") setAnswer({ on: got.on });
    else setNote(t(language, "journal.lala.failed"));
  }, [language]);

  const state = answer ? journalLalaState(answer) : JOURNAL_LALA_STATES.UNKNOWN;
  return <JournalLalaSettingPanel state={state} busy={busy} note={note} language={language} onAct={act} />;
}
